import React, { useEffect, useState, useMemo, useRef } from 'react';
import styled from 'styled-components';

import Drawer from '../common/Drawer';
import { Controller } from 'react-hook-form';
import {
	CrewRolesDDOptions,
	User,
	UserPermissionsDDOptions,
} from '../../data/models/user';
import { Grid, Typography, Box } from '@mui/material';
import { Tabs as MuiTabs, Tab } from '@mui/material';
import Input from '../common/form/Input';
import MaskInput from '../common/form/MaskInput';
import Autocomplete from '../common/form/AutoComplete';
import SaveCancelButtons from '../common/buttons/SaveCancelButtons';
import BusySpinner from '../common/BusySpinner';
import { updateUser } from '../../data/thunks/userThunk';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../data/store';
import { fetchUserGroups } from '../../data/thunks/userGroupThunk';
import * as GoogleStorageService from '../../services/google-storage.service';
import { showAlert } from '../../data/stores/alertStore';
import { processImageForUpload } from '../../utils/image-processing';
import Checkbox from '../common/form/Checkbox';
import Button from '../common/buttons/Button';
import Dialog, {
	DialogContent,
	DialogTitle,
	DialogActions,
} from '../common/Dialog';
import Avatar from '../common/Avatar';
import CopyableText from '../common/CopyableText';
import AutoComplete from '../common/form/AutoComplete';
import ProfileAvatar from '../common/ProfileAvatar';

// Import crew form hook and internal onboarding components
import { useCrewForm } from './hooks/useCrewForm';
import {
	EditInternalFormValues,
	buildEditInternalSavePayload,
	createEditInternalDefaultValues,
	mapUserToEditInternalFormValues,
} from './onboarding/internal/editFormUtils';
import InternalBasicInformationStep from './onboarding/internal/BasicInformationStep';
import WorkPersonalPreferencesStep from './onboarding/internal/WorkPersonalPreferencesStep';
import TravelInformationStep from './onboarding/internal/TravelInformationStep';

interface EditUserModalProps {
	open: boolean;
	handleClose: () => void;
	user?: User;
}

export default function EditUserModal(props: EditUserModalProps) {
	const { open, user, handleClose } = props;
	const [currentTab, setCurrentTab] = useState(0);
	const [showSubscribeDialog, setShowSubscribeDialog] = useState(false);
	const [showUnsubscribeDialog, setShowUnsubscribeDialog] = useState(false);
	const [isSaving, setIsSaving] = useState(false);
	const [updatedProfilePicture, setUpdatedProfilePicture] = useState<
		string | null
	>(null);
	const isLoadingGroupsRef = useRef(false);

	const dispatch = useDispatch<AppDispatch>();
	const { userGroups } = useSelector(
		(state: RootState) => state.userGroupState
	);
	const { company } = useSelector((state: RootState) => state.companyState);
	const { astroToken } = useSelector((state: RootState) => state.authState);

	const editInternalDefaultValues = React.useMemo(
		() => createEditInternalDefaultValues(),
		[]
	);

	const handleEditInternalSave = React.useCallback(
		async (payload: any) => {
			if (!user?.id) {
				return;
			}
			if (!payload || Object.keys(payload).length === 0) {
				return;
			}
			await dispatch(updateUser({ id: user.id, ...payload }));
		},
		[dispatch, user?.id]
	);

	const {
		control,
		errors,
		setValue,
		formValues,
		handleSubmit,
		reset,
		hydrateForm,
		saveProgress,
	} = useCrewForm<EditInternalFormValues, User, any, any, void, void>({
		defaultValues: editInternalDefaultValues,
		mode: 'onBlur',
		reValidateMode: undefined,
		hydrate: mapUserToEditInternalFormValues,
		buildSavePayload: (values, helpers) =>
			buildEditInternalSavePayload(values, helpers.isFieldDirty),
		onSave: handleEditInternalSave,
	});

	const resetForm = (user?: User) => {
		if (user) {
			hydrateForm(user);
		} else {
			reset(editInternalDefaultValues);
		}
	};

	// Convert user groups to AutoComplete options format
	const groupOptions = useMemo(() => {
		return userGroups.list.map((group) => ({
			id: group.id,
			display: group.name,
		}));
	}, [userGroups.list]);

	// Convert selected groups to AutoComplete format
	const selectedGroupOptions = useMemo(() => {
		return (formValues.groups || []).map((group) => ({
			id: group.id,
			display: group.name,
		}));
	}, [formValues.groups]);

	const handleGroupsChange = (event: any, newValue: any) => {
		// Convert AutoComplete options back to UserGroup objects
		const selectedGroups = newValue
			.map((option: any) => {
				return userGroups.list.find((group) => group.id === option.id);
			})
			.filter(Boolean);
		setValue('groups', selectedGroups, { shouldDirty: true });
	};

	// Fetch user groups when component mounts
	useEffect(() => {
		if (
			company?.id &&
			!userGroups.initialized &&
			!isLoadingGroupsRef.current
		) {
			isLoadingGroupsRef.current = true;
			dispatch(
				fetchUserGroups({ companyId: company.id, take: 1000 })
			).finally(() => {
				isLoadingGroupsRef.current = false;
			});
		}
	}, [dispatch, company?.id, userGroups.initialized]);

	useEffect(() => {
		resetForm(user);
		setUpdatedProfilePicture(null);
	}, [user, open]);

	const handleOnSaveClick = async (data: EditInternalFormValues) => {
		setIsSaving(true);
		await saveProgress();
		resetForm();
		handleCancelCloseOnClick();
		setIsSaving(false);
	};

	const handleCancelCloseOnClick = () => {
		resetForm();
		setUpdatedProfilePicture(null);
		handleClose();
		setCurrentTab(0);
	};

	const handleSubscribe = () => {
		if (user?.id) {
			dispatch(updateUser({ id: user.id, status: 'SUBSCRIBED' }));
			setShowSubscribeDialog(false);
			handleCancelCloseOnClick();
		}
	};

	const handleUnsubscribe = () => {
		if (user?.id) {
			dispatch(updateUser({ id: user.id, status: 'UNSUBSCRIBED' }));
			setShowUnsubscribeDialog(false);
			handleCancelCloseOnClick();
		}
	};

	const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
		setCurrentTab(newValue);
	};

	const handleProfilePhotoDrop = React.useCallback(
		async (acceptedFiles: File[]) => {
			const file = acceptedFiles[0];
			if (!file || !user?.id) return;

			try {
				// Process the image (convert HEIC/HEIF and resize if needed)
				const { processedFile } = await processImageForUpload(file);

				const path = `${company?.id}/profile-pictures/${processedFile.name}`;
				const response = await GoogleStorageService.uploadFile(
					processedFile,
					path,
					astroToken
				);

				// Get a signed GET URL for displaying the uploaded image
				const signedGetUrl = await GoogleStorageService.getSignedGetUrl(
					path,
					astroToken
				);

				// Update user with new profile picture URL
				await dispatch(
					updateUser({
						id: user.id,
						profilePictureUrl: response,
					} as any)
				);

				// Update local state immediately so avatar updates
				setUpdatedProfilePicture(signedGetUrl);

				dispatch(
					showAlert({
						message: 'Profile picture uploaded successfully',
						severity: 'success',
					})
				);
			} catch (error) {
				console.error('Error uploading profile picture:', error);
				dispatch(
					showAlert({
						message: 'Error uploading profile picture',
						severity: 'error',
					})
				);
			}
		},
		[user?.id, dispatch, astroToken]
	);

	const renderBasicInformationTab = () => (
		<Container>
			<ScrollableContent>
				<InternalBasicInformationStep
					formValues={formValues}
					setValue={setValue}
					control={control}
					errors={errors}
					isRequired={false}
				/>
			</ScrollableContent>
			<ButtonContainer>
				<SaveCancelButtons
					handleSave={handleSubmit(handleOnSaveClick)}
					handleCancel={handleCancelCloseOnClick}
					disabled={isSaving}
				/>
			</ButtonContainer>
		</Container>
	);

	const renderWorkPersonalTab = () => (
		<Container>
			<ScrollableContent>
				<WorkPersonalPreferencesStep
					formValues={formValues}
					setValue={setValue}
					control={control}
					errors={errors}
					isRequired={false}
				/>
			</ScrollableContent>
			<ButtonContainer>
				<SaveCancelButtons
					handleSave={handleSubmit(handleOnSaveClick)}
					handleCancel={handleCancelCloseOnClick}
					disabled={isSaving}
				/>
			</ButtonContainer>
		</Container>
	);

	const renderTravelTab = () => (
		<Container>
			<ScrollableContent>
				<TravelInformationStep
					formValues={formValues}
					setValue={setValue}
					control={control}
					errors={errors}
					isRequired={false}
				/>
			</ScrollableContent>
			<ButtonContainer>
				<SaveCancelButtons
					handleSave={handleSubmit(handleOnSaveClick)}
					handleCancel={handleCancelCloseOnClick}
					disabled={isSaving}
				/>
			</ButtonContainer>
		</Container>
	);

	const renderOverviewTab = () => (
		<Container>
			{/* Avatar Section */}
			<ProfileAvatar
				profilePictureUrl={
					updatedProfilePicture ||
					user?.userProfile?.profilePictureUrl?.publicUrl ||
					user?.googleProfilePictureUrl
				}
				firstName={user?.firstName}
				lastName={user?.lastName}
				onDrop={handleProfilePhotoDrop}
			/>

			<Grid container spacing={2}>
				{/* First Name and Last Name side by side */}
				<Grid item xs={6} md={6}>
					<Controller
						name={'firstName'}
						control={control}
						rules={{ required: true }}
						render={({ field }) => (
							<Input
								{...field}
								id={'firstName'}
								label={'Firstname'}
								fullWidth
								error={errors.firstName}
								helperText={errors.firstName?.message}
							/>
						)}
					/>
				</Grid>
				<Grid item xs={6} md={6}>
					<Controller
						name={'lastName'}
						control={control}
						rules={{ required: true }}
						render={({ field }) => (
							<Input
								{...field}
								id={'lastName'}
								label={'Lastname'}
								fullWidth
								error={errors.lastName}
								helperText={errors.lastName?.message}
							/>
						)}
					/>
				</Grid>

				{/* Email as copyable text */}
				<Grid item xs={12} md={12}>
					<Box>
						<Typography
							variant="body2"
							color="text.secondary"
							sx={{ mb: 1 }}
						>
							Email
						</Typography>
						<CopyableText text={user?.email || ''} />
					</Box>
				</Grid>

				{/* Phone Number */}
				<Grid item xs={12} md={12}>
					<Controller
						name={'phone'}
						control={control}
						render={({ field }) => (
							<MaskInput
								{...field}
								id={'phone'}
								label={'Phone Number'}
								mask="phone"
								fullWidth
							/>
						)}
					/>
				</Grid>

				{/* Permissions */}
				<Grid item xs={12} md={12}>
					<Controller
						name={'permissions'}
						control={control}
						rules={{ required: true }}
						render={({ field }) => (
							<Autocomplete
								{...field}
								id={'permissions'}
								label={'Permissions'}
								options={UserPermissionsDDOptions}
								onChange={(e, value) => {
									field.onChange(value?.id || '');
								}}
								error={!!errors.permissions}
								helperText={errors.permissions?.message}
							/>
						)}
					/>
				</Grid>

				{/* Role */}
				<Grid item xs={12} md={12}>
					<Controller
						name={'role'}
						control={control}
						rules={{ required: true }}
						render={({ field }) => (
							<Autocomplete
								{...field}
								id={'role'}
								label={'Role'}
								options={CrewRolesDDOptions}
								onChange={(e, value) => {
									field.onChange(value?.id || '');
								}}
								error={!!errors.role}
								helperText={errors.role?.message}
							/>
						)}
					/>
				</Grid>

				{/* Groups */}
				<Grid item xs={12} md={12}>
					<Controller
						name={'groups'}
						control={control}
						render={({ field }) => (
							<AutoComplete
								id={'groups'}
								label={'Groups'}
								options={groupOptions}
								value={selectedGroupOptions}
								onChange={handleGroupsChange}
								multiple={true}
								placeholder="Select groups..."
								error={!!errors.groups}
								helperText={errors.groups?.message}
							/>
						)}
					/>
				</Grid>

				{/* Always add to projects */}
				<Grid item xs={12} md={12}>
					<Controller
						name={'alwaysAddToProjects'}
						control={control}
						render={({ field }) => {
							return (
								<Checkbox
									size={'small'}
									items={[
										{
											id: 'alwaysAddToProjects',
											name: 'Always add to projects',
											checked: !!field.value,
											handleOnChange: () =>
												field.onChange(!field.value),
										},
									]}
								/>
							);
						}}
					/>
				</Grid>

				{/* Subscribe/Unsubscribe */}
				<Grid item xs={12} md={12}>
					{user?.status === 'SUBSCRIBED' && (
						<Button
							variant="text"
							color="error"
							onClick={() => setShowUnsubscribeDialog(true)}
						>
							Unsubscribe
						</Button>
					)}
					{user?.status === 'UNSUBSCRIBED' && (
						<Button
							variant="text"
							color="primary"
							onClick={() => setShowSubscribeDialog(true)}
						>
							Subscribe
						</Button>
					)}
				</Grid>
			</Grid>

			<ButtonContainer>
				<SaveCancelButtons
					handleSave={handleSubmit(handleOnSaveClick)}
					handleCancel={handleCancelCloseOnClick}
					disabled={isSaving}
				/>
			</ButtonContainer>
		</Container>
	);

	if (!open || !user) return <></>;

	return (
		<>
			<Drawer
				open={open}
				handleClose={handleCancelCloseOnClick}
				title="Edit Staff Member"
				width={600}
			>
				<TabsContainer>
					{isSaving && (
						<BusyOverlay>
							<BusySpinner display="COMPONENT" />
						</BusyOverlay>
					)}
					<Box
						sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}
					>
						<MuiTabs
							value={currentTab}
							onChange={handleTabChange}
							aria-label="user tabs"
							variant="scrollable"
							scrollButtons="auto"
						>
							<Tab label="Overview" />
							<Tab label="Basic Information" />
							<Tab label="Work & Personal" />
							<Tab label="Travel" />
						</MuiTabs>
					</Box>
					<TabPanel value={currentTab} index={0}>
						{renderOverviewTab()}
					</TabPanel>
					<TabPanel value={currentTab} index={1}>
						{renderBasicInformationTab()}
					</TabPanel>
					<TabPanel value={currentTab} index={2}>
						{renderWorkPersonalTab()}
					</TabPanel>
					<TabPanel value={currentTab} index={3}>
						{renderTravelTab()}
					</TabPanel>
				</TabsContainer>
			</Drawer>

			<Dialog
				open={showSubscribeDialog}
				handleClose={() => setShowSubscribeDialog(false)}
			>
				<DialogTitle>Subscribe User</DialogTitle>
				<DialogContent>
					<Typography>
						Are you sure you want to subscribe {<b>{user.email}</b>}
						? This will give them access to Orbito.
						{/* adjust your company's subscription cost
						based on the addition of a new user. */}
					</Typography>
				</DialogContent>
				<DialogActions>
					<Button
						onClick={() => setShowSubscribeDialog(false)}
						variant="outlined"
					>
						Cancel
					</Button>
					<Button onClick={handleSubscribe} color="primary">
						Subscribe
					</Button>
				</DialogActions>
			</Dialog>

			<Dialog
				open={showUnsubscribeDialog}
				handleClose={() => setShowUnsubscribeDialog(false)}
			>
				<DialogTitle>Unsubscribe User</DialogTitle>
				<DialogContent>
					<Typography>
						Are you sure you want to unsubscribe{' '}
						{<b>{user.email}</b>}? This user will lose access to
						Orbito and your company's subscription cost will be
						updated based on the removal of a user.
					</Typography>
				</DialogContent>
				<DialogActions>
					<Button
						onClick={() => setShowUnsubscribeDialog(false)}
						variant="outlined"
					>
						Cancel
					</Button>
					<Button onClick={handleUnsubscribe} color="error">
						Unsubscribe
					</Button>
				</DialogActions>
			</Dialog>
		</>
	);
}

interface TabPanelProps {
	children?: React.ReactNode;
	index: number;
	value: number;
}

function TabPanel(props: TabPanelProps) {
	const { children, value, index, ...other } = props;

	return (
		<div
			role="tabpanel"
			hidden={value !== index}
			id={`simple-tabpanel-${index}`}
			aria-labelledby={`simple-tab-${index}`}
			style={{ height: '100%', overflow: 'auto', marginBottom: '20px' }}
			{...other}
		>
			{value === index && <Box sx={{ height: '100%' }}>{children}</Box>}
		</div>
	);
}

const Container = styled.div`
	display: flex;
	flex-direction: column;
	height: 100%;
`;

const TabsContainer = styled.div`
	height: 100%;
	display: flex;
	flex-direction: column;
	padding-bottom: 20px;
`;

const ScrollableContent = styled.div`
	flex: 1;
	overflow-y: auto;
	padding-right: 8px;
`;

const ButtonContainer = styled.div`
	position: absolute;
	bottom: 16px;
	right: 16px;
`;

const BusyOverlay = styled.div`
	position: absolute;
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
	z-index: 10;
	display: flex;
	align-items: center;
	justify-content: center;
`;
